import React, { useState, useEffect, useCallback } from 'react';
import api from '@/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Paperclip, Upload, Download, Trash2, FileText, Image, File } from 'lucide-react';

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getFileIcon = (contentType: string) => {
  if (contentType?.startsWith('image/')) return Image;
  if (contentType?.includes('pdf') || contentType?.includes('document')) return FileText;
  return File;
};

interface FileAttachmentsProps {
  entityType: 'opportunity' | 'contact';
  entityId: string;
}

export default function FileAttachments({ entityType, entityId }: FileAttachmentsProps) {
  const [files, setFiles] = useState<any[]>([]);
  const [uploading, setUploading] = useState(false);

  const fetchFiles = useCallback(async () => {
    if (!entityId) return;
    try {
      const { data } = await api.get('/attachments', { params: { entity_type: entityType, entity_id: entityId } });
      setFiles(data);
    } catch { /* silent */ }
  }, [entityType, entityId]);

  useEffect(() => { fetchFiles(); }, [fetchFiles]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Archivo demasiado grande (max 10MB)');
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('entity_type', entityType);
    formData.append('entity_id', entityId);
    try {
      await api.post('/attachments/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Archivo adjuntado');
      fetchFiles();
    } catch { toast.error('Error al subir archivo'); }
    setUploading(false);
    e.target.value = '';
  };

  const handleDownload = async (attachment: any) => {
    try {
      const response = await api.get(`/attachments/${attachment.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachment.original_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Error al descargar'); }
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/attachments/${id}`);
      toast.success('Archivo eliminado');
      fetchFiles();
    } catch { toast.error('Error al eliminar'); }
  };

  if (!entityId) return null;

  return (
    <div className="mt-4 border-t border-[#E2E8F0] pt-4" data-testid="file-attachments">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Paperclip size={15} className="text-[#475569]" />
          <span className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Archivos Adjuntos ({files.length})</span>
        </div>
        <div className="relative">
          <input type="file" onChange={handleUpload} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" data-testid="attachment-upload-input" />
          <Button variant="outline" size="sm" className="border-[#E2E8F0] text-[#475569] pointer-events-none h-7 text-xs" disabled={uploading}>
            <Upload size={12} className="mr-1" /> {uploading ? 'Subiendo...' : 'Adjuntar'}
          </Button>
        </div>
      </div>
      {files.length > 0 ? (
        <div className="space-y-2">
          {files.map((f) => {
            const IconComp = getFileIcon(f.content_type);
            return (
              <div key={f.id} className="flex items-center justify-between p-2 rounded border border-[#E2E8F0] bg-[#F8FAFC] hover:bg-[#F1F5F9] transition-colors" data-testid={`attachment-${f.id}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <IconComp size={16} className="text-[#2563EB] shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-[#0F172A] truncate">{f.original_name}</p>
                    <p className="text-[10px] text-[#94A3B8]">{formatFileSize(f.size)} - {f.uploaded_by_name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleDownload(f)} data-testid={`download-${f.id}`}>
                    <Download size={12} className="text-[#475569]" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleDelete(f.id)} data-testid={`delete-attachment-${f.id}`}>
                    <Trash2 size={12} className="text-[#EF4444]" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-xs text-[#94A3B8] text-center py-3">Sin archivos adjuntos</p>
      )}
    </div>
  );
}
